/**
 * Resumable chunked import for LARGE files (e.g. long video) on the project
 * Import screen. A single multipart POST of a multi-GB file dies on flaky
 * links and blows past the server's request-body cap; this splits the file
 * into chunks sized to the link, retries each chunk on its own (waiting out
 * a dropped connection), then asks the server
 * to assemble them and create tasks — the SAME import path a normal upload
 * uses (unlike the editor's chunkedUpload helper, which passes
 * commit_to_project=false to store a media asset without a task).
 *
 * Backend: label_studio/data_import/chunked_api.py
 *   POST /api/projects/:pk/chunked-uploads/     -> { id }
 *   POST /api/chunked-uploads/:id/chunk/  (xN)  -> append (index + blob)
 *   POST /api/chunked-uploads/:id/complete/     -> { task_ids, ... }
 */

// Chunk size follows the link: start small, aim for a chunk every few
// seconds, so a dropped connection costs seconds of upload and not minutes.
// The server caps a chunk at 20MB and takes chunks of any size, in order.
const CHUNK_START = 1024 * 1024;
const CHUNK_MIN = 256 * 1024;
const CHUNK_MAX = 8 * 1024 * 1024;
const CHUNK_TARGET_SECONDS = 8;
// Waits between attempts at one chunk, in seconds. A link that is down for
// a minute or two is ordinary; the upload should outlast it.
const RETRY_WAITS = [1, 2, 4, 8, 15, 30, 30];
const OFFLINE_WAIT_MS = 10 * 60 * 1000;

/** Files at/above this size go through the chunked path; smaller ones ride
 *  the normal single-request multipart POST (fewer round-trips). A photo
 *  from a phone is a few MB, which on a slow link is already long enough to
 *  be worth resuming. Cloud Run also refuses any single request body above
 *  32 MB. */
export const IMPORT_CHUNK_THRESHOLD_BYTES = 4 * 1024 * 1024; // 4MB

/** Resolve when the browser reports a connection again (or after a cap, so
 *  a wrong ``navigator.onLine`` cannot hang the upload). */
export function waitForOnline(maxMs = OFFLINE_WAIT_MS): Promise<void> {
  if (typeof navigator === "undefined" || navigator.onLine !== false) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener("online", done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, maxMs);
    window.addEventListener("online", done);
  });
}

/** A response worth trying again: the server or the link failed, not the
 *  request. */
export const isTransientStatus = (status?: number) => !status || status >= 500 || status === 408 || status === 429;

function csrfToken(): string {
  const m = typeof document !== "undefined" ? document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/) : null;
  return m ? decodeURIComponent(m[1]) : "";
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Upload one File in chunks. With ``commit`` (the default) the server
 *  creates tasks from it; without, the file is stored and waits for the
 *  Import step to be saved. Resolves with the parsed complete-response
 *  JSON. */
export async function chunkedImportFile(
  projectId: number | string,
  file: File,
  opts: { onProgress?: (fraction: number) => void; commit?: boolean } = {},
): Promise<any> {
  const csrf = csrfToken();

  const createResp = await fetch(`/api/projects/${projectId}/chunked-uploads/`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRFToken": csrf },
    body: JSON.stringify({ filename: file.name, total_size: file.size }),
  });
  if (!createResp.ok) {
    let detail = `HTTP ${createResp.status}`;
    try {
      detail = (await createResp.json())?.detail || detail;
    } catch {
      /* keep status */
    }
    throw new Error(`Could not start upload for ${file.name}: ${detail}`);
  }
  const { id } = await createResp.json();
  if (!id) throw new Error(`Upload for ${file.name} returned no session id`);

  const total = file.size;
  let size = CHUNK_START;
  let start = 0;

  for (let i = 0; start < total || i === 0; i++) {
    let end = Math.min(total, start + size);
    let lastErr: unknown = null;
    let ok = false;
    for (let attempt = 0; attempt <= RETRY_WAITS.length; attempt++) {
      await waitForOnline();
      const began = Date.now();
      try {
        const fd = new FormData();
        fd.append("chunk", file.slice(start, end), `${file.name}.part${i}`);
        fd.append("index", String(i));
        const r = await fetch(`/api/chunked-uploads/${id}/chunk/`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "X-CSRFToken": csrf },
          body: fd,
        });
        if (!r.ok) {
          lastErr = new Error(`HTTP ${r.status}`);
          if (!isTransientStatus(r.status)) break;
          throw lastErr;
        }
        ok = true;
        const seconds = Math.max(0.2, (Date.now() - began) / 1000);
        size = Math.round(Math.min(CHUNK_MAX, size * 2, Math.max(CHUNK_MIN, (size * CHUNK_TARGET_SECONDS) / seconds)));
        break;
      } catch (e) {
        lastErr = e;
        if (attempt === RETRY_WAITS.length) break;
        // A chunk that did not get through is sent again smaller. The
        // server replaces a chunk sent twice, so the next one follows on.
        size = Math.max(CHUNK_MIN, Math.round(size / 2));
        end = Math.min(total, start + size);
        await sleep(RETRY_WAITS[attempt] * 1000);
      }
    }
    if (!ok) {
      throw new Error(
        `Upload of ${file.name} stopped at ${Math.round((start / Math.max(1, total)) * 100)}%: ${lastErr instanceof Error ? lastErr.message : String(lastErr)}`,
      );
    }
    start = end;
    opts.onProgress?.(total ? end / total : 1);
    if (start >= total) break;
  }

  // Assemble, and create tasks unless the file only waits for the Import
  // step (commit_to_project defaults to true server-side).
  const query = opts.commit === false ? "?commit_to_project=false" : "";
  const doneResp = await fetch(`/api/chunked-uploads/${id}/complete/${query}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRFToken": csrf },
  });
  if (!doneResp.ok) {
    let detail = `HTTP ${doneResp.status}`;
    try {
      detail = (await doneResp.json())?.detail || detail;
    } catch {
      /* keep status */
    }
    throw new Error(`Could not finish importing ${file.name}: ${detail}`);
  }
  return doneResp.json();
}
